import { useEffect, useState } from "react";
import { useManageAsesmenStore } from "../manage-asesmen.store";
import RegionalItem from "../component/regional-item";
import { Plus, X } from "@phosphor-icons/react";
import BaseModal from "../../../components/modal/base";
import { FormInput } from "../../../components/forms/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { FormSelectAsync } from "../../../components/forms/input-select";
import { City } from "../../../types/common";
import { FormTextArea } from "../../../components/forms/input-textarea";
import { createRegion } from "../manage-asesmen.service";
import { useAlert } from "../../../stores/alert";
import { Button } from "../../../components/buttons";
import { Spinner } from "flowbite-react";
import { request } from "../../../api/config";
import { InputSearch } from "../../../components/forms/input-search";
import Pagination from "../../../components/tables/pagination";

const createRegionalPayload = z.object({
  city_ids: z
    .array(
      z.object({
        name: z.string(),
        id: z.union([z.string(), z.number()]),
      })
    )
    .min(1, "At least one city ID is required"),
  name: z.string().min(1, "Name is required"),
  notes: z.string().optional(),
  description: z.string().optional(),
});

type CreateRegionalScheme = z.infer<typeof createRegionalPayload>;

type ErrorForm = {
  city_ids: [] | null;
  name: [] | null;
  notes: [] | null;
  description: [] | null;
};

const AsesmenRegional = () => {
  const [page, setPage] = useState<number>(1);
  const [q, setQ] = useState<string>("");
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorForm | undefined>(undefined);

  const { getRegions, loadingRegions, regions } = useManageAsesmenStore();
  const { setMessage } = useAlert();

  const {
    control,
    handleSubmit,
    reset,
    // formState: { errors },
  } = useForm<CreateRegionalScheme>({
    resolver: zodResolver(createRegionalPayload),
    defaultValues: {
      city_ids: [],
      name: "",
      notes: "",
      description: "",
    },
  });

  const selectCities = async (inputValue: string) => {
    let params = {
      q: inputValue,
    };
    const { data } = await request.get("/select/cities", {
      params: params,
    });
    return data.data;
  };

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        city_ids: data.city_ids.map((item) => item.id),
      };
      await createRegion(payload);
      setModalCreate(false);
      setMessage("Berhasil menyimpan data", "success");
      reset({
        city_ids: [],
        name: "",
        description: "",
        notes: "",
      });
      getRegions();
    } catch (err: any) {
      setErrors(err?.response?.data?.errors);
    } finally {
      setLoading(false);
    }
  });

  const handleNext = () => {
    if (page === regions?.last_page) {
      return;
    }

    setPage(page + 1);
  };

  const handlePrevious = () => {
    if (page === 1) {
      return;
    }

    setPage(page - 1);
  };

  useEffect(() => {
    getRegions({ page, offset: 10, q });
  }, [getRegions, page, q]);

  return (
    <>
      <div className="flex items-center justify-between">
        <strong>Area Regional</strong>
        <button
          className="flex items-center gap-1 bg-blue-600 px-2 hover:bg-blue-700 rounded-lg text-white"
          onClick={() => setModalCreate(true)}
        >
          <Plus size={12} />
          <span className="text-sm">Buat</span>
        </button>
      </div>
      <div className="w-full py-2">
        <div className="relative">
          <input
            className="w-full border rounded-lg p-2 text-sm pr-12"
            placeholder="Cari.."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <div className="absolute top-2.5 right-2">
              <button
                className="text-red-600 hover:text-red-700"
                onClick={() => setQ("")}
              >
                <X size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="mt-2">
        {loadingRegions && (
          <>
            <div className="w-full min-h-[30vh] flex items-center justify-center">
              <div className="flex items-center gap-3">
                <Spinner />
                <span className="text-sm text-gray-500">Loading ...</span>
              </div>
            </div>
          </>
        )}
        {regions?.data.map((item, key) => (
          <RegionalItem key={key} item={item} />
        ))}
      </div>

      <Pagination
        currentPage={regions?.current_page ?? 1}
        totalPage={regions?.last_page ?? 1}
        onNext={handleNext}
        onPrevious={handlePrevious}
      />

      <BaseModal
        title={`Tambah Area Regional Baru`}
        isOpen={modalCreate}
        close={() => setModalCreate(false)}
        size="2xl"
      >
        <div className="py-4">
          <FormInput
            label="Nama Regional"
            required
            control={control}
            name="name"
          />
          <FormSelectAsync
            label="Pilih Kota"
            name="city_ids"
            control={control}
            loadOption={selectCities}
            multiple={true}
            required
            optionLabel={(option: City) => `${option.name}`}
            optionValue={(option: City) => `${option.id}`}
            error={errors?.city_ids}
          />
          <FormTextArea
            label="Deskripsi"
            control={control}
            name="description"
          />
        </div>
        <div className="mt-4 flex items-center justify-end">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setModalCreate(false)}
              variant="danger"
              className="px-4"
            >
              Batal
            </Button>
            <Button onClick={onSubmit} className="px-10">
              {loading ? <Spinner /> : "Iya!"}
            </Button>
          </div>
        </div>
      </BaseModal>
    </>
  );
};

export default AsesmenRegional;
